import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FormattedDateInput from '../../../components/ui/FormattedDateInput';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { IconWarningTriangle } from '../../../components/icons/WizardIcons';
import { useAttorneyGeneralWizardContext } from '../../../hooks/useCaseRequestWizard';
import { calcAgeFromDob, exceedsMax, maxCharLimitExceededError, todayIsoDateLocal } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors, type RequiredFieldSpec } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { AttorneyGeneralWizardActions } from './AttorneyGeneralWizardActions';
import { attorneyGeneralWizardPath, AttorneyGeneralWizardShell, FieldError, inpClass } from './AttorneyGeneralWizardShell';
import { agEnterPlaceholder, agSelectPlaceholder } from './attorneyGeneralPlaceholders';
import { useAttorneyGeneralLookups } from './useAttorneyGeneralLookups';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_NAME = 255;

export default function AttorneyGeneralStep4VictimDetailsPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useAttorneyGeneralWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const maxLen = useWizardMaxLengthChange(setErrors);
  const { genders, loading: gendersLoading } = useAttorneyGeneralLookups('step4');

  const maxDob = useMemo(() => todayIsoDateLocal(), []);
  const age = useMemo(() => calcAgeFromDob(draft.victimDob), [draft.victimDob]);
  const under16 = typeof age === 'number' && age < 16;

  const validate = (): boolean => {
    const extra: RequiredErrors = {};
    if (exceedsMax(draft.victimName, MAX_NAME)) extra.victimName = maxCharLimitExceededError();
    setErrors(extra);
    if (Object.keys(extra).length === 0) return true;
    const specs: RequiredFieldSpec[] = [
      { key: 'victimName', label: f.victimName, value: draft.victimName, elementId: 'ag-victimName' },
    ];
    focusFirstInvalid(specs, extra);
    return false;
  };

  return (
    <AttorneyGeneralWizardShell currentStep={4} draft={draft}>
      <SectionCard title={f.sectionVictimDetails}>
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.victimName}</label>
            <input
              id="ag-victimName"
              className={inpClass(Boolean(errors.victimName))}
              type="text"
              value={draft.victimName}
              onChange={(e) => setDraft((d) => ({ ...d, victimName: maxLen('victimName', e.target.value, MAX_NAME) }))}
              placeholder={f.enterVictimName}
            />
            <FieldError message={errors.victimName} />
          </div>

          <div className="ccr-fg">
            <label>{f.dateOfBirth}</label>
            <FormattedDateInput
              value={draft.victimDob}
              maxDate={maxDob}
              enableYmdViews
              placeholder={f.dateOfBirth}
              onChange={(next) => setDraft((d) => ({ ...d, victimDob: next }))}
            />
            {under16 ? (
              <div className="under16">
                <IconWarningTriangle />
                Child is under 16 years
              </div>
            ) : null}
          </div>
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
          <div className="ccr-fg">
            <label>{f.age}</label>
            <input className="ccr-inp" type="text" value={typeof age === 'number' ? String(age) : ''} readOnly disabled placeholder="" />
          </div>
          <div className="ccr-fg">
            <label>{f.gender}</label>
            <WizardSelect
              value={draft.victimGenderId}
              disabled={gendersLoading}
              placeholder={f.selectGender}
              options={genders.map((g) => ({ id: g.id, label: g.name }))}
              onChange={(id) => {
                const name = genders.find((g) => g.id === id)?.name ?? '';
                setDraft((d) => ({ ...d, victimGenderId: id, victimGenderName: name }));
              }}
            />
          </div>
        </div>
      </SectionCard>

      <AttorneyGeneralWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={4}
        onBack={() => nav(attorneyGeneralWizardPath(3, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(attorneyGeneralWizardPath(5, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </AttorneyGeneralWizardShell>
  );
}
